import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DIRECTUS_ARTICLES_ENDPOINT, DIRECTUS_INSTANCE, fetchCurrentUser, formatDate } from '../../api';
import Heading from '../../ui/atoms/Typography/Heading';
import Text from '../../ui/atoms/Typography/Text';
import EmptyState from './components/EmptyState';
import {
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  CircleStackIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { AgentDashboardGrid, AgentQuickActionsWidget, FunnelKpiWidget } from './widgets';
import { useWidgetFunnel } from './hooks/useWorkspaceStatistics';
import { attachFirstMessageTitles, buildAgentDashboardStats } from './utils/agentDashboardStats';
import { getCurrentWeekRange } from './utils/dashboardDateRange';
import { widgetFooterLinkClass } from './widgets/widgetAnalyticsUi';
import { getDesignStudioUrl } from '../../features/design-studio';
import { isCurrentPipelineSession } from '@prosecaptain/sentiment-rules';
import createProjectIcon from '../../img/create-project.svg';
import provideDataIcon from '../../img/provide-data.svg';
import createContentIcon from '../../img/create-content.svg';
import chatIcon from '../../img/icons/chat.png';

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isAgentMode = location.pathname.startsWith('/agent');
  const writerArticlesPath = '/writer/articles';
  const writerCreateArticlePath = '/writer/articles/create-article/step/setup';
  const [name, setName] = useState('');
  const [recentArticles, setRecentArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(true);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [chatStats, setChatStats] = useState(null);
  const [loadingChatStats, setLoadingChatStats] = useState(true);
  const [hasAgent, setHasAgent] = useState(false);
  const [hasKnowledge, setHasKnowledge] = useState(false);
  const [loadingAgentSetup, setLoadingAgentSetup] = useState(false);
  const [userContextLoaded, setUserContextLoaded] = useState(false);
  const [primaryAgentId, setPrimaryAgentId] = useState(null);
  const weekRange = useMemo(() => getCurrentWeekRange(), []);
  // Widget funnel (widget_events_daily), last 30 days; null hides the card.
  const { funnel: widgetFunnel } = useWidgetFunnel(isAgentMode ? workspaceId : null, 30);
  const statsOverviewHref = primaryAgentId
    ? `/agent/ai-agents/${primaryAgentId}?tab=overview`
    : '/agent/ai-agents';

  const regularQuickActions = useMemo(() => (
    isAgentMode
      ? [
          {
            name: t('dashboard.features.browseConversations', { defaultValue: 'Przeglądaj konwersacje' }),
            icon: ChatBubbleLeftRightIcon,
            link: '/agent/assistants'
          },
          {
            name: t('dashboard.features.analyzeConversationData', { defaultValue: 'Analizuj dane konwersacji' }),
            icon: ChartBarIcon,
            link: primaryAgentId ? `/agent/ai-agents/${primaryAgentId}?tab=overview` : '/agent/ai-agents'
          },
          {
            name: t('dashboard.features.dataCollection', { defaultValue: 'Zarządzaj wiedzą' }),
            icon: CircleStackIcon,
            link: '/agent/datasets'
          }
        ]
      : [
        {
          name: t('dashboard.features.createContent', { defaultValue: 'Create Article' }),
          description: t('dashboard.features.createContentDesc', { defaultValue: 'Generate AI-powered articles' }),
          icon: createProjectIcon,
          link: writerCreateArticlePath,
          bgColor: 'bg-white',
          iconColor: 'text-primary-400'
        },
        {
          name: t('dashboard.features.dataCollection', { defaultValue: 'Manage Knowledge' }),
          description: t('dashboard.features.dataCollectionDesc', { defaultValue: 'Manage your knowledge datasets' }),
          icon: provideDataIcon,
          link: '/datasets',
          bgColor: 'bg-white',
          iconColor: 'text-orange-400'
        },
        {
          name: t('dashboard.features.createImages', { defaultValue: 'Create Dedicated Images' }),
          description: t('dashboard.features.createImagesDesc', { defaultValue: 'Generate custom images for your content' }),
          icon: createContentIcon,
          link: getDesignStudioUrl(),
          external: true,
          bgColor: 'bg-white',
          iconColor: 'text-red-400'
        }
      ]
  ), [isAgentMode, primaryAgentId, t, writerCreateArticlePath]);

  const onboardingQuickActions = useMemo(() => ([
    {
      name: 'Dodaj nowego agenta',
      description: 'Utwórz pierwszego AI Agenta dla swojej strony',
      icon: createProjectIcon,
      link: '/agent/ai-agents/create-widget',
      disabled: false
    },
    {
      name: 'Dodaj wiedzę do agenta',
      description: 'Dodaj źródła wiedzy, żeby agent odpowiadał na podstawie Twoich danych',
      icon: provideDataIcon,
      link: '/agent/datasets/create-dataset',
      disabled: !hasAgent
    },
    {
      name: 'Analizuj konwersacje',
      description: 'Przeglądaj rozmowy i wyciągaj wnioski z interakcji użytkowników',
      icon: chatIcon,
      link: '/agent/conversations',
      disabled: !hasAgent || !hasKnowledge
    }
  ]), [hasAgent, hasKnowledge]);

  const showAgentOnboarding = isAgentMode && !loadingAgentSetup && !hasAgent;

  // Fetch user data and name
  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem('directus_token');
      if (!token) {
        setUserContextLoaded(true);
        return;
      }

      try {
        const userData = await fetchCurrentUser(['first_name', 'last_name', 'linked_workspace.id']);
        setName(userData?.first_name || '');
        setWorkspaceId(userData?.linked_workspace?.id || null);
        localStorage.setItem('username', userData?.first_name || '');
      } catch (error) {
        process.env.NODE_ENV === "development" && console.error('Error fetching user data:', error);
      } finally {
        setUserContextLoaded(true);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('directus_token');

    if (!isAgentMode || !token) {
      setLoadingAgentSetup(false);
      return;
    }

    if (!workspaceId) {
      return;
    }

    let cancelled = false;

    const fetchAgentSetupState = async () => {
      try {
        setLoadingAgentSetup(true);

        const [widgetsResponse, datasetsResponse] = await Promise.all([
          axios.get(`${DIRECTUS_INSTANCE}/items/widget_config`, {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              fields: ['id'],
              limit: 1,
              filter: JSON.stringify({
                widget_linked_workspace: { _eq: workspaceId }
              })
            }
          }),
          axios.get(`${DIRECTUS_INSTANCE}/items/data_sets`, {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              fields: ['id'],
              limit: 1,
              filter: JSON.stringify({
                linked_workspace: { _eq: workspaceId }
              })
            }
          })
        ]);

        if (cancelled) return;

        const widgets = widgetsResponse.data?.data || [];
        setHasAgent(widgets.length > 0);
        setPrimaryAgentId(widgets[0]?.id || null);
        setHasKnowledge((datasetsResponse.data?.data || []).length > 0);
      } catch (error) {
        if (!cancelled) {
          process.env.NODE_ENV === "development" && console.error('Error fetching onboarding state:', error);
          setHasAgent(false);
          setHasKnowledge(false);
          setPrimaryAgentId(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingAgentSetup(false);
        }
      }
    };

    fetchAgentSetupState();

    return () => {
      cancelled = true;
    };
  }, [isAgentMode, workspaceId]);

  // Fetch recent articles with thumbnails
  useEffect(() => {
    const fetchRecentArticles = async () => {
      const token = localStorage.getItem('directus_token');
      const userId = localStorage.getItem('user_id');
      
      if (!token || !userId) {
        setLoadingArticles(false);
        return;
      }

      try {
        const filter = {
          _and: [
            { user_created: { id: { _eq: userId } } },
            { status: { _neq: 'archived' } }
          ]
        };

        if (workspaceId) {
          filter._and.push({
            _or: [
              { article_linked_workspace: { _eq: workspaceId } },
              { article_linked_workspace: { _null: true } }
            ]
          });
        }

        const response = await axios.get(DIRECTUS_ARTICLES_ENDPOINT, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            filter,
            limit: 6,
            sort: '-date_created',
            fields: 'id,title,slug,date_created,status,type,article_thumbnail_url'
          }
        });

        setRecentArticles(response.data.data || []);
      } catch (error) {
        process.env.NODE_ENV === "development" && console.error('Error fetching recent articles:', error);
        setRecentArticles([]);
      } finally {
        setLoadingArticles(false);
      }
    };

    if (workspaceId !== null) {
      fetchRecentArticles();
    }
  }, [workspaceId]);

  // Fetch agent dashboard analytics (KPI, chart, attention, leads)
  useEffect(() => {
    const fetchChatAnalytics = async () => {
      if (!workspaceId || !isAgentMode) {
        setLoadingChatStats(false);
        return;
      }

      const token = localStorage.getItem('directus_token');
      if (!token) {
        setLoadingChatStats(false);
        return;
      }

      try {
        setLoadingChatStats(true);

        const { startDate, endDate } = weekRange;

        const sessionsResponse = await axios.get(`${DIRECTUS_INSTANCE}/items/chat_sessions`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            filter: JSON.stringify({
              _and: [
                { linked_workspace: { _nnull: true } },
                { linked_workspace: { _eq: workspaceId } },
                { date_created: { _gte: startDate.toISOString() } },
                { date_created: { _lte: endDate.toISOString() } },
                { overall_sentiment: { _nnull: true } }
              ]
            }),
            fields: [
              'id',
              'title',
              'date_created',
              'overall_sentiment',
              'messages_count',
              'intent_fulfilled',
              'resolution_status',
              'priority_band',
              'business_risk',
              'pipeline_version'
            ],
            limit: 1000,
            sort: '-date_created'
          }
        });

        let sessions = sessionsResponse.data.data || [];
        const hasCurrentPipeline = sessions.some(isCurrentPipelineSession);
        if (hasCurrentPipeline) {
          sessions = sessions.filter(isCurrentPipelineSession);
        }
        const sessionIds = sessions.map((s) => s.id);

        const attentionIds = sessions
          .filter((s) => {
            const p = s.priority_band || s.business_risk;
            return p === 'P0' || p === 'P1';
          })
          .sort((a, b) => new Date(b.date_created) - new Date(a.date_created))
          .slice(0, 5)
          .map((s) => s.id);

        const requests = [];

        if (sessionIds.length > 0) {
          requests.push(
            axios.get(`${DIRECTUS_INSTANCE}/items/chat_messages`, {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                filter: JSON.stringify({
                  _and: [
                    { session_id: { _in: sessionIds } },
                    { date_created: { _gte: startDate.toISOString() } },
                    { date_created: { _lte: endDate.toISOString() } }
                  ]
                }),
                fields: ['id', 'date_created', 'session_id'],
                limit: 1000
              }
            })
          );
        } else {
          requests.push(Promise.resolve({ data: { data: [] } }));
        }

        if (attentionIds.length > 0) {
          requests.push(
            axios.get(`${DIRECTUS_INSTANCE}/items/chat_messages`, {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                filter: JSON.stringify({
                  _and: [
                    { session_id: { _in: attentionIds } },
                    { role: { _eq: 'user' } }
                  ]
                }),
                fields: ['session_id', 'content', 'order', 'date_created'],
                limit: attentionIds.length * 3
              }
            })
          );
        } else {
          requests.push(Promise.resolve({ data: { data: [] } }));
        }

        if (sessionIds.length > 0) {
          requests.push(
            axios.get(`${DIRECTUS_INSTANCE}/items/crm`, {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                filter: JSON.stringify({
                  linked_session: { _in: sessionIds }
                }),
                fields: [
                  'id',
                  'lead_first_name',
                  'lead_last_name',
                  'lead_email',
                  'status',
                  'date_created'
                ],
                sort: '-date_created',
                limit: 5,
                meta: 'filter_count'
              }
            })
          );
        } else {
          requests.push(Promise.resolve({ data: { data: [], meta: { filter_count: 0 } } }));
        }

        const [messagesResponse, attentionMessagesResponse, leadsResponse] = await Promise.all(requests);
        const messages = messagesResponse.data.data || [];

        const firstMessagesBySession = {};
        (attentionMessagesResponse.data.data || []).forEach((message) => {
          if (!firstMessagesBySession[message.session_id]) {
            firstMessagesBySession[message.session_id] = [];
          }
          firstMessagesBySession[message.session_id].push(message);
        });
        Object.keys(firstMessagesBySession).forEach((sessionId) => {
          firstMessagesBySession[sessionId].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          firstMessagesBySession[sessionId] = firstMessagesBySession[sessionId][0]?.content || null;
        });

        sessions = attachFirstMessageTitles(sessions, firstMessagesBySession);
        const totalLeads = leadsResponse.data.meta?.filter_count || 0;
        const recentLeads = (leadsResponse.data.data || []).map((lead) => ({
          id: lead.id,
          lead_first_name: lead.lead_first_name,
          lead_last_name: lead.lead_last_name,
          lead_email: lead.lead_email,
          status: lead.status,
          dateCreated: lead.date_created
        }));

        setChatStats(buildAgentDashboardStats({
          sessions,
          messages,
          totalLeads,
          recentLeads,
          rangeStart: startDate,
          rangeEnd: endDate,
          locale: i18n.language
        }));
      } catch (error) {
        process.env.NODE_ENV === "development" && console.error('Error fetching chat analytics:', error);
        setChatStats(null);
      } finally {
        setLoadingChatStats(false);
      }
    };

    if (workspaceId) {
      fetchChatAnalytics();
    }
  }, [workspaceId, isAgentMode, weekRange, i18n.language]);

  const recent = useMemo(() => {
    return recentArticles.slice(0, 6);
  }, [recentArticles]);

  // Helper to get thumbnail URL
  const getThumbnailUrl = (article) => {
    if (!article.article_thumbnail_url) return null;
    
    if (typeof article.article_thumbnail_url === 'string') {
      return article.article_thumbnail_url;
    }
    
    if (Array.isArray(article.article_thumbnail_url) && article.article_thumbnail_url.length > 0) {
      const chosen = article.article_thumbnail_url.find(thumb => thumb.chosen);
      return chosen ? chosen.url : article.article_thumbnail_url[0].url;
    }
    
    return null;
  };

  const handleQuickActionClick = (action) => {
    if (action.disabled) {
      return;
    }

    if (action.link?.startsWith('#')) {
      const target = document.querySelector(action.link);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    if (action.external) {
      window.open(action.link, '_blank', 'noopener,noreferrer');
    } else {
      navigate(action.link);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 bg-neutral-100 pt-4 pb-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Heading 
            level={1} 
            className="text-2xl sm:text-2xl mb-1 font-normal"
          >
            {showAgentOnboarding
              ? `Cześć, ${name || 'Adwokat'}`
              : t('dashboard.greeting', { name: name || t('dashboard.there', { defaultValue: 'there' }) })}
          </Heading>
          <Text 
            size="lg" 
            color="muted"
            className="text-lg sm:text-lg text-neutral-800"
          >
            {showAgentOnboarding ? 'Co chcesz dziś zrobić?' : t('dashboard.whatToDo')}
          </Text>
        </div>

        {isAgentMode && !showAgentOnboarding && !loadingAgentSetup && userContextLoaded && (
          <AgentQuickActionsWidget
            actions={regularQuickActions}
            onActionClick={handleQuickActionClick}
          />
        )}

        {isAgentMode && (!userContextLoaded || loadingAgentSetup) ? (
          <div className="mb-10 bg-white border border-gray-200 rounded-xl p-10">
            <div className="flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
            </div>
          </div>
        ) : (
          <>
            {showAgentOnboarding && (
            <div className="mb-6">
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-4">
                Szybkie akcje
              </div>
              <div className="space-y-4">
                  <div className="relative overflow-hidden rounded-2xl border border-primary-200 bg-gradient-to-r from-primary-50 via-white to-primary-100 p-6 sm:p-8">
                    <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                      <div className="max-w-xl">
                        <div className="mb-2 inline-flex items-center rounded-full border border-primary-200 bg-white px-3 py-1 text-xs font-medium text-primary-700">
                          Krok 1
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900">{onboardingQuickActions[0].name}</h3>
                        <p className="mt-2 text-sm text-gray-600">{onboardingQuickActions[0].description}</p>
                        <button
                          type="button"
                          onClick={() => handleQuickActionClick(onboardingQuickActions[0])}
                          className="mt-5 inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
                        >
                          + Konfiguruj agenta
                        </button>
                      </div>
                      <div className="relative flex h-24 w-24 items-center justify-center rounded-2xl bg-white/80 ring-1 ring-primary-200">
                        <img src={onboardingQuickActions[0].icon} alt={onboardingQuickActions[0].name} className="h-14 w-14" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {onboardingQuickActions.slice(1).map((action, idx) => (
                      <button
                        key={action.name}
                        type="button"
                        disabled={action.disabled}
                        onClick={() => handleQuickActionClick(action)}
                        className={`text-left border rounded-2xl px-5 py-5 transition-all ${
                          action.disabled
                            ? 'bg-neutral-100 border-gray-200 opacity-60 cursor-not-allowed'
                            : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-primary-300'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div className="max-w-xl">
                            <div className="mb-2 inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-600">
                              Krok {idx + 2}
                            </div>
                            <div className="text-sm font-semibold text-gray-900 mb-1">{action.name}</div>
                            <div className="text-[13px] text-gray-500">{action.description}</div>
                          </div>
                          <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-white/80 ring-1 ring-primary-200">
                            <img src={action.icon} alt={action.name} className="h-14 w-14" />
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
            </div>
            )}

            {!isAgentMode && (
            <div className="mb-6">
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-4">
                {t('dashboard.quickActions', { defaultValue: 'Quick Actions' })}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {regularQuickActions.map((action, index) => (
                  <button
                    key={index}
                    type="button"
                    disabled={action.disabled}
                    onClick={() => handleQuickActionClick(action)}
                    className={`text-left border rounded-xl px-5 pb-5 pt-4 transition-all ${
                      action.disabled
                        ? 'bg-neutral-100 border-gray-200 opacity-60 cursor-not-allowed'
                        : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-primary-300'
                    }`}
                  >
                    <div className="flex flex-col items-start mb-3">
                      <div className="w-16 h-16 rounded-xl bg-white border border-gray-100 flex items-center justify-center mb-3">
                        <img 
                          src={action.icon} 
                          alt={action.name}
                          className="w-[52px] h-[52px]"
                        />
                      </div>
                      <div className="text-sm font-semibold text-gray-900 mb-1">{action.name}</div>
                      <div className="text-[13px] text-gray-500">{action.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            )}
          </>
        )}

        {/* Recently Created Articles */}
        {!isAgentMode && (
          loadingArticles ? (
            <div className="mb-10">
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-4">
                {t('dashboard.recentArticles', { defaultValue: 'Recently Created' })}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <div className="aspect-square bg-gray-100 animate-pulse" />
                    <div className="p-3 space-y-2">
                      <div className="h-3 bg-gray-200 rounded w-3/4 animate-pulse" />
                      <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : recent.length > 0 ? (
            <div className="mb-10">
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-4">
                {t('dashboard.recentArticles', { defaultValue: 'Recently Created' })}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {recent.map((article) => {
                  const thumbnailUrl = getThumbnailUrl(article);
                  return (
                    <button
                      key={article.id}
                      onClick={() => navigate(`${writerArticlesPath}/${article.id}`)}
                      className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-primary-300 hover:bg-gray-50 transition-all text-left group"
                    >
                      <div className="aspect-square bg-gradient-to-br from-primary-50 to-primary-100 overflow-hidden flex items-center justify-center relative">
                        {thumbnailUrl ? (
                          <img 
                            src={thumbnailUrl} 
                            alt={article.title || 'Article thumbnail'}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="text-primary-400 text-2xl font-bold">
                            {article.title?.charAt(0)?.toUpperCase() || 'A'}
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <div className="text-xs text-gray-900 truncate group-hover:text-primary-600 transition-colors mb-1">
                          {article.title || 'Untitled'}
                        </div>
                        <div className="text-[10px] text-gray-500">
                          {formatDate(article.date_created)}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="mb-10">
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-4">
                {t('dashboard.recentArticles', { defaultValue: 'Recently Created' })}
              </div>
              <EmptyState 
                icon={<img src={createContentIcon} alt="" className="h-10 w-10 object-contain" />}
                title={t('dashboard.noArticles', { defaultValue: 'No articles yet' })}
                description={t('dashboard.noArticlesDesc', { defaultValue: 'Start creating AI-powered articles to see them here' })}
                action={
                  <button
                    onClick={() => navigate(writerCreateArticlePath)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                  >
                    {t('dashboard.createFirstArticle', { defaultValue: 'Create your first article' })}
                  </button>
                }
              />
            </div>
          )
        )}

        {/* Chat Analytics Widget */}
        {isAgentMode && !showAgentOnboarding && (
        <div id="chat-analytics" className="mb-0">
          <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-gray-900 font-sans">
              {t('dashboard.agent.sectionTitle', { defaultValue: 'Agent overview' })}
            </h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-sm text-gray-500 font-sans">{t('dashboard.currentWeek')}</span>
              <button
                type="button"
                onClick={() => navigate(statsOverviewHref)}
                className={widgetFooterLinkClass}
              >
                {t('dashboard.agent.viewDetailedReports')}
                <ArrowRightIcon className="ml-1 h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {loadingChatStats ? (
            <AgentDashboardGrid loading primaryAgentId={primaryAgentId} onNavigate={navigate} />
          ) : chatStats ? (
            <AgentDashboardGrid
              loading={false}
              stats={chatStats}
              primaryAgentId={primaryAgentId}
              onNavigate={navigate}
            />
          ) : (
            <EmptyState 
              icon={<img src={chatIcon} alt="" className="h-10 w-10 object-contain" />}
              title={t('dashboard.noChatData', { defaultValue: 'No chat data available' })}
              description={t('dashboard.noChatDataDesc', { defaultValue: 'Create a widget to start collecting chat analytics and engaging with your visitors' })}
              action={
                <button
                  onClick={() => navigate('/agent/ai-agents')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                >
                  {t('dashboard.createWidget', { defaultValue: 'Create a widget' })}
                </button>
              }
            />
          )}
          {!loadingChatStats && widgetFunnel && (
            <div className="mt-5">
              <FunnelKpiWidget funnel={widgetFunnel} dateRange={30} />
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
